// The small pieces every App Banners form is drawn with, so the forms cannot drift apart.

import type { ReactNode } from 'react';
import { InfoDot } from '../hiring/HiringForm';

/** A text box, as the platform's forms draw it. */
export const FIELD_INPUT =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500';

/** A field's name, its (i) if it has one, and anything that belongs at the row's far end. */
export function FieldLabel({
  text,
  hint,
  htmlFor,
  children,
}: {
  text: string;
  hint?: string;
  htmlFor?: string;
  children?: ReactNode;
}) {
  const className = 'text-sm font-medium text-gray-700';
  return (
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="flex min-w-0 items-center gap-1">
        {htmlFor ? (
          <label htmlFor={htmlFor} className={className}>
            {text}
          </label>
        ) : (
          <span className={className}>{text}</span>
        )}
        {hint && <InfoDot text={hint} label={text} />}
      </span>
      {children}
    </div>
  );
}

/** One choice out of a few, in a row of equal buttons. */
export function PillRow<T extends string | number>({
  options,
  value,
  onChange,
  small = false,
}: {
  options: { value: T; label: string; title?: string }[];
  value: T;
  onChange: (next: T) => void;
  small?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={value === o.value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`flex-1 whitespace-nowrap rounded-lg border-2 px-2 py-[7px] text-center font-medium leading-5 transition-colors ${
            small ? 'text-xs' : 'text-sm'
          } ${
            value === o.value
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A choice with a line of its own: a task to send, or a test to fire. As RoleCards draws them. */
export function ChoiceCard({
  label,
  line,
  tag,
  title,
  on,
  onClick,
}: {
  label: string;
  line: string;
  /** A word beside the name, as the mockup's "New app". */
  tag?: string;
  title?: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      title={title}
      onClick={onClick}
      className={`block min-w-0 rounded-xl bg-white text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
        on ? 'border-2 border-primary-600 px-[13px] py-[7px]' : 'border border-gray-200 px-3.5 py-2 hover:border-primary-300'
      }`}
    >
      {/* On a phone's two columns the tag drops under the name rather than cutting it. */}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
        <span className="max-w-full truncate text-sm font-medium text-gray-900">{label}</span>
        {tag && <span className="flex-none whitespace-nowrap text-[11px] font-medium text-amber-700">{tag}</span>}
      </span>
      <span className="block truncate text-xs text-gray-500">{line}</span>
    </button>
  );
}
